import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import SendInvite from "./SendInvite";

const PublicProfile = () => {
  const { user_id } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);

  // Stage 1: "I’d like to work with you" intent state
  const [currentUserId, setCurrentUserId] = useState(null);
  const [hasIntent, setHasIntent] = useState(false);
  const [intentSaving, setIntentSaving] = useState(false);

  // Load creator profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user_id) return;
      const { data, error } = await supabase
        .from("creator_profiles")
        .select("*")
        .eq("user_id", user_id)
        .single();

      if (error) {
        console.error("Error fetching profile:", error.message);
      } else {
        setProfile(data);
      }

      setLoading(false);
    };

    fetchProfile();
  }, [user_id]);

  // Load current viewer + whether they already expressed intent
  useEffect(() => {
    const fetchIntentState = async () => {
      try {
        const { data: userData, error: userErr } = await supabase.auth.getUser();
        if (userErr) {
          console.error("Error fetching current user:", userErr.message);
          return;
        }

        const viewerId = userData?.user?.id || null;
        setCurrentUserId(viewerId);

        // No viewer logged in, or viewing own profile → no need to check
        if (!viewerId || !user_id || viewerId === user_id) return;

        // Check if an intent already exists
        const { data: existingIntent, error: intentErr } = await supabase
          .from("creator_intents")
          .select("id")
          .eq("from_user_id", viewerId)
          .eq("to_user_id", user_id)
          .eq("intent_type", "work_with")
          .maybeSingle();

        if (intentErr) {
          console.error("Error checking intent:", intentErr.message);
          return;
        }

        if (existingIntent) {
          setHasIntent(true);
        }
      } catch (err) {
        console.error("Unexpected error while checking intent:", err);
      }
    };

    fetchIntentState();
  }, [user_id]);

  const handleExpressIntent = async () => {
    // Not logged in → send to login
    if (!currentUserId) {
      navigate("/login");
      return;
    }

    // Don’t allow expressing interest in oneself
    if (currentUserId === user_id) {
      return;
    }

    // Already expressed intent
    if (hasIntent) return;

    setIntentSaving(true);
    try {
      const { error } = await supabase
        .from("creator_intents")
        .upsert(
          {
            from_user_id: currentUserId,
            to_user_id: user_id,
            intent_type: "work_with",
            status: "open",
          },
          {
            onConflict: "from_user_id,to_user_id,intent_type",
          }
        );

      if (error) {
        console.error("Error saving intent:", error.message);
        alert("Unable to record your interest right now. Please try again.");
      } else {
        setHasIntent(true);
      }
    } catch (err) {
      console.error("Unexpected error saving intent:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setIntentSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-transparent text-slate-100">
        <div className="mx-auto max-w-4xl px-4 py-10 text-center">
          <p className="text-sm text-slate-300">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-full bg-transparent text-slate-100">
        <div className="mx-auto max-w-4xl px-4 py-10 text-center">
          <p className="text-sm text-rose-300">❌ Profile not found.</p>
        </div>
      </div>
    );
  }

  const tools =
    profile.tools?.split(",").map((t) => t.trim()).filter(Boolean) || [];
  const portfolioLinks =
    profile.portfolio_links
      ?.split(",")
      .map((link) => link.trim())
      .filter(Boolean) || [];

  const viewingOwnProfile = currentUserId && currentUserId === user_id;

  return (
    <div className="min-h-full bg-transparent text-slate-100">
      <div className="mx-auto max-w-5xl px-4 py-6 md:py-8">
        {/* Heading */}
        <h2 className="text-xl font-semibold text-cyan-300 md:text-2xl">
          👁️ Viewing Creator Profile
        </h2>
        <p className="mt-2 text-xs md:text-sm text-slate-300">
          On Dreamframe, every user can both create and collaborate. Tap{" "}
          <span className="font-semibold">“I’d like to work with you”</span> to
          signal your interest — we’ll use this to prioritize future matches and
          intros.
        </p>

        {/* Main grid */}
        <div className="mt-5 grid gap-4 md:gap-5 md:grid-cols-3">
          {/* Summary card */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 text-center shadow-lg shadow-fuchsia-500/20 backdrop-blur">
            <img
              src={
                profile.profile_image_url?.trim()
                  ? profile.profile_image_url
                  : "/user.png"
              }
              alt="avatar"
              className="mx-auto h-24 w-24 rounded-full border-2 border-cyan-400/70 object-cover shadow-md shadow-cyan-500/40"
            />
            <h3 className="mt-3 text-base font-semibold text-slate-100">
              {profile.first_name}
            </h3>
            <p className="text-xs text-slate-400">{profile.role}</p>
            {profile.tagline && (
              <p className="mt-3 text-xs text-slate-200">
                <span className="font-semibold text-slate-100">Tagline: </span>
                {profile.tagline}
              </p>
            )}
          </div>

          {/* Creative profile */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
            <h4 className="text-sm font-semibold text-sky-200">
              Preferred Genres
            </h4>
            <p className="mt-1 text-xs text-slate-200">
              {profile.genres || "Not specified yet"}
            </p>

            <h4 className="mt-4 text-sm font-semibold text-sky-200">
              Top Tools Used
            </h4>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {tools.length ? (
                tools.map((tool, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-cyan-500/10 px-2.5 py-0.5 text-[11px] text-cyan-200"
                  >
                    {tool}
                  </span>
                ))
              ) : (
                <p className="text-xs text-slate-400">No tools listed yet.</p>
              )}
            </div>

            <h4 className="mt-4 text-sm font-semibold text-sky-200">Bio</h4>
            <p className="mt-1 text-xs leading-relaxed text-slate-200 whitespace-pre-wrap">
              {profile.bio || "No bio added yet."}
            </p>
          </div>

          {/* Collaboration preferences */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
            <h4 className="text-sm font-semibold text-emerald-200">
              General Availability
            </h4>
            <p className="mt-1 text-xs text-slate-200">
              {profile.availability || "Not specified"}
            </p>

            <h4 className="mt-4 text-sm font-semibold text-emerald-200">
              Time Zone
            </h4>
            <p className="mt-1 text-xs text-slate-200">
              {profile.time_zone || "Not specified"}
            </p>

            <h4 className="mt-4 text-sm font-semibold text-emerald-200">
              Preferred Work Style
            </h4>
            <p className="mt-1 text-xs text-slate-200">
              {profile.collab_style || "Not specified"}
            </p>
          </div>
        </div>

        {/* Portfolio section */}
        <div className="mt-6 rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
          <h4 className="text-sm font-semibold text-sky-200">Portfolio Links</h4>
          {portfolioLinks.length ? (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-200">
              {portfolioLinks.map((link, i) => (
                <li key={i}>
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cyan-300 underline decoration-cyan-400/70 underline-offset-2 hover:text-cyan-200"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-slate-400">
              No portfolio links added yet.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-sm">
          {/* Stage 1: primary intent button (only if not viewing own profile) */}
          {!viewingOwnProfile && (
            <button
              type="button"
              onClick={handleExpressIntent}
              disabled={intentSaving || hasIntent}
              className={`inline-flex items-center rounded-2xl px-5 py-2 text-xs font-semibold shadow-md transition ${
                hasIntent
                  ? "bg-emerald-500/20 text-emerald-200 border border-emerald-400/60 cursor-default"
                  : "bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 text-white shadow-fuchsia-500/40 hover:brightness-110"
              }`}
            >
              {hasIntent
                ? "✅ You’ve expressed interest"
                : intentSaving
                ? "Saving your interest..."
                : "I’d like to work with you"}
            </button>
          )}

          {/* Original Invite to Collaborate button (hidden for Stage 1, logic preserved) */}
          {false && (
            <button
              type="button"
              onClick={() => {
                setShowInviteForm((prev) => {
                  const newState = !prev;
                  if (newState) {
                    setTimeout(() => {
                      const inviteSection = document.getElementById(
                        "inviteForm"
                      );
                      if (inviteSection) {
                        inviteSection.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      } else {
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }, 100);
                  }
                  return newState;
                });
              }}
              className={`inline-flex items-center rounded-2xl px-5 py-2 text-xs font-semibold shadow-md transition ${
                showInviteForm
                  ? "bg-rose-500 text-white shadow-rose-500/30 hover:bg-rose-400"
                  : "bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 text-white shadow-fuchsia-500/40 hover:brightness-110"
              }`}
            >
              {showInviteForm ? "❌ Cancel Invite" : "🤝 Invite to Collaborate"}
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center rounded-2xl border border-slate-600 bg-slate-900 px-5 py-2 text-xs font-semibold text-slate-100 shadow-sm hover:bg-slate-800"
          >
            ⬅ Back to Dashboard
          </button>
        </div>

        {/* Invite form (hidden for Stage 1, logic preserved) */}
        {false && showInviteForm && (
          <div
            id="inviteForm"
            className="mt-6 rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-lg shadow-fuchsia-500/20 backdrop-blur"
          >
            <SendInvite receiverId={user_id} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicProfile;
